import { runtimeConfig } from "config/runtimeConfig";
import React, { useState, useCallback, useRef, useEffect } from 'react';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import { LoadingState } from './DefineModel/model';
import { uploadProductModel } from './DefineModel/ModelServices/ModalServices';
import LoadingSuccessErrorAlert from 'Components/GMS/Rules/Components/LoadingSuccessErrorAlert';

const API_BASE_URL = runtimeConfig.GMS_BACKEND_API_URL || '';

type UploadModalProps = {
    id: string;
    showModal: boolean;
    closeModal: () => void;
    channelKey: string;
    onUploadSuccess?: () => void;
};

/**
 * The product-model XLS uploader on the DS Dialog (§ 98). The upload state
 * machine — .xlsx validation, the inputKey reset trick, upload → job_id →
 * 2-second recursive status polling, auto-close after success, the unmount
 * guard — is byte-identical. What changed is the shell and chrome: DS Dialog
 * (Escape/scrim/X all route through the SAME `handleClose` guard the footer
 * Close always used, so closing mid-upload is still refused — now with the
 * warning toast on every path instead of a silently-disabled X), and DS
 * Buttons in the footer band (Delete on § 84's danger variant, retiring the
 * Bootstrap `btn-outline-danger`; GMSButton/GMSFillButton retire too).
 *
 * The file picker stays a hidden <input type="file"> behind a styled label ON
 * PURPOSE. TODO(design-system): there is no DS FileUpload/Dropzone (and no
 * upload glyph in Iconography); mint one only when a second uploader appears.
 */
export const UploadModal = ({
    id,
    showModal,
    closeModal,
    channelKey,
    onUploadSuccess
}: UploadModalProps) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [inputKey, setInputKey] = useState('');
    const [loading, setLoading] = useState<LoadingState>({
        status: '',
        message: ''
    });

    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const resetFileInput = useCallback(() => {
        const randomString = Math.random().toString(36);
        setInputKey(randomString);
    }, []);

    const handleFileSelection = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];

            if (!file) {
                setSelectedFile(null);
                return;
            }

            if (!file.name.endsWith('.xlsx')) {
                toast.error('Only .xlsx files are allowed');
                setSelectedFile(null);
                return;
            }

            setSelectedFile(file);
        },
        []
    );

    const handleFileDelete = useCallback(() => {
        setSelectedFile(null);
        resetFileInput();
    }, [resetFileInput]);

    const checkUploadStatus = useCallback(async (job_id: string) => {
        if (!isMountedRef.current || !job_id) return;

        try {
            const statusResponse = await ServiceWrapper.doGet(
                `${API_BASE_URL}job/status/${job_id}`,
                null,
                null,
                AuthManager.getGmsToken()
            );

            if (!statusResponse.errorfound) {
                const { data } = statusResponse;

                if (data?.error) {
                    toast.error(data.status || 'Upload failed');
                    setLoading({
                        status: 'Error',
                        message: data.status || 'Upload failed'
                    });
                    return;
                }

                if (data?.processing) {
                    setLoading({
                        status: 'Loading',
                        message: data.status || 'Processing...'
                    });

                    setTimeout(() => {
                        checkUploadStatus(job_id);
                    }, 2000);
                    return;
                }

                // Upload completed
                toast.success(data.status || 'Upload completed successfully');
                setLoading({
                    status: 'Success',
                    message: data.status || 'Upload completed successfully'
                });

                if (onUploadSuccess) {
                    onUploadSuccess();
                }

                setTimeout(() => {
                    if (isMountedRef.current) {
                        closeModal();
                        setLoading({ status: '', message: '' });
                    }
                }, 2000);
            } else {
                toast.error('Error checking upload status');
                setLoading({
                    status: 'Error',
                    message: 'Error checking upload status'
                });
            }
        } catch (error) {
            console.error('Error occurred while checking upload status:', error);
            toast.error('Error during upload');
            setLoading({
                status: 'Error',
                message: 'Error during upload'
            });
        }
    }, [closeModal, onUploadSuccess]);

    const handleUpload = useCallback(async () => {
        if (!selectedFile) {
            toast.error('Please select a file');
            return;
        }

        setLoading({
            status: 'Loading',
            message: 'File uploading...'
        });

        const result = await uploadProductModel(selectedFile, channelKey);

        if (!result.done || !result.job_id) {
            toast.error(result.error || 'Error uploading file');
            setLoading({
                status: 'Error',
                message: result.error || 'Error uploading file'
            });
            resetFileInput();
            setSelectedFile(null);
            return;
        }

        const { done, message, job_id } = result;

        if (!done) {
            toast.error(message);
            setLoading({
                status: 'Error',
                message
            });
            resetFileInput();
            setSelectedFile(null);
            return;
        }

        resetFileInput();
        setSelectedFile(null);

        if (job_id) {
            setTimeout(() => {
                checkUploadStatus(job_id);
            }, 2000);
        } else {
            toast.error('Job ID not found');
            setLoading({
                status: 'Error',
                message: 'Job ID not found'
            });
        }
    }, [selectedFile, channelKey, resetFileInput, checkUploadStatus]);

    const handleClose = useCallback(() => {
        if (loading.status === 'Loading') {
            toast.warning('Upload in progress. Please wait...');
            return;
        }

        setSelectedFile(null);
        setLoading({ status: '', message: '' });
        resetFileInput();
        closeModal();
    }, [loading.status, closeModal, resetFileInput]);

    const isUploading = loading.status === 'Loading';

    return (
        <Dialog
            isOpen={showModal}
            onClose={handleClose}
            title="Upload XLS"
            data-testid={`${id}-modal`}
            footer={
                <div className="tw-flex tw-w-full tw-items-center tw-justify-between">
                    <Button
                        variant="danger"
                        data-testid="pages-uploadmodal-button-2"
                        id={id}
                        disabled={!selectedFile}
                        onClick={handleFileDelete}
                    >
                        Delete
                    </Button>
                    <div className="tw-flex tw-gap-3">
                        <Button
                            variant="secondary"
                            data-testid={`${id}-close-btn`}
                            onClick={() => handleClose()}
                        >
                            Close
                        </Button>
                        <Button
                            variant="primary"
                            data-testid={`${id}-save-btn`}
                            disabled={!selectedFile || isUploading}
                            onClick={() => handleUpload()}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="tw-border tw-border-edge tw-rounded-ctl tw-p-5">
                <label className="tw-block tw-w-full tw-text-center tw-text-accent-bold tw-cursor-pointer">
                    <input
                        type="file"
                        id={`${id}-file-input`}
                        className="tw-hidden"
                        key={inputKey || ''}
                        onChange={handleFileSelection}
                        onClick={(event) => {
                            (event.target as HTMLInputElement).value = '';
                        }}
                        accept=".xlsx"
                        disabled={isUploading}
                        data-testid={`${id}-file-input`}
                    />
                    <i className="fa fa-cloud-upload fa-x" aria-hidden="true"></i>
                    <Typography variant="sub-body-supporting" as="span" className="tw-block tw-mt-1 tw-text-ink">
                        (Note: Only .xlsx files are allowed to upload)
                    </Typography>
                    <Typography variant="title" as="span" className="tw-block tw-mt-1 tw-font-semibold">
                        Browse File
                    </Typography>
                </label>
                {selectedFile ? (
                    <Typography variant="body-primary" as="div" className="tw-mt-2 tw-text-center tw-break-words">
                        {selectedFile.name}
                    </Typography>
                ) : null}
            </div>
            <div className="tw-mt-4">
                <LoadingSuccessErrorAlert loading={loading} hide_error={true} handle_reload={null} />
            </div>
        </Dialog>
    );
};
